import { useEffect, useMemo, useState } from 'react'
import { useAwardsIndex } from './useAwards'
import {
  summarizeBodies,
  topMovies,
  topPersons,
  type AwardBodySummary,
} from './awards'
import { BodyPicker } from './BodyPicker'
import { BodyDetail } from './BodyDetail'
import { LeaderboardSection } from './LeaderboardSection'
import { SearchByFilm } from './SearchByFilm'

/**
 * Optional pre-selection forwarded from the detail modals when the
 * user clicks a citation in the compact awards section. LibraryView
 * passes `{ bodyWikidataId, editionId }` which drills straight into
 * `BodyDetail` with the matching ceremony already selected. A `nonce`
 * lets the parent re-fire the same jump (e.g. clicking the same
 * citation twice) by bumping it — otherwise the effect below would
 * no-op on repeat clicks.
 */
export interface AwardsJumpTarget {
  bodyWikidataId: string
  editionId: string
  nonce: number
}

interface Props {
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  onPersonOpen: (personId: number) => void
  jumpTarget?: AwardsJumpTarget | null
}

type Tab = 'ceremonies' | 'leaderboards' | 'search'

/**
 * PWA parity port of the desktop Awards browser
 * (`src/renderer/components/views/AwardsView.tsx` +
 * `AwardDetailView.tsx`). Three tabs — Ceremonies, Leaderboards and
 * Search by film — all fed by the same flat `/awards.json` snapshot
 * loaded once by `useAwardsIndex`.
 *
 * The opens-into-body flow lives in state here (`openBodyId`) rather
 * than in a router so the user's tab/scroll position is preserved when
 * they back out of a ceremony.
 */
export function AwardsView({ onOpenDetail, onPersonOpen, jumpTarget }: Props) {
  const { index, loading, error } = useAwardsIndex()
  const [tab, setTab] = useState<Tab>('ceremonies')
  const [openBodyId, setOpenBodyId] = useState<string | null>(null)
  const [pendingEditionId, setPendingEditionId] = useState<string | null>(null)

  const bodies: AwardBodySummary[] = useMemo(
    () => (index ? summarizeBodies(index) : []),
    [index]
  )
  const movies = useMemo(() => (index ? topMovies(index, 40) : []), [index])
  const people = useMemo(() => (index ? topPersons(index, 40) : []), [index])

  // Deep-link handling: when LibraryView forwards a jump target (user
  // clicked a citation in the compact Awards section), force the tab
  // to "ceremonies", open the target body and hand BodyDetail the
  // preferred edition. `jumpTarget.nonce` is the trigger so re-clicking
  // the same citation re-fires the effect.
  useEffect(() => {
    if (!jumpTarget) return
    setTab('ceremonies')
    setOpenBodyId(jumpTarget.bodyWikidataId)
    setPendingEditionId(jumpTarget.editionId)
  }, [jumpTarget])

  // ── Loading / error guards ──
  if (loading) {
    return <AwardsSkeleton />
  }
  if (error || !index) {
    return (
      <div className="px-4 py-16 text-center">
        <div className="mx-auto max-w-md rounded-xl border border-accent-danger/30 bg-accent-danger/5 p-6 text-sm text-accent-danger">
          {error ?? 'Awards data unavailable. Check your connection and try again.'}
        </div>
      </div>
    )
  }

  // ── Body detail ("ceremonies" drill-down) ──
  if (openBodyId) {
    const body = bodies.find((b) => b.wikidataId === openBodyId)
    if (body) {
      return (
        <div className="h-[calc(100vh-8rem)]">
          <BodyDetail
            index={index}
            body={body}
            initialEditionId={pendingEditionId}
            onBack={() => {
              setPendingEditionId(null)
              setOpenBodyId(null)
            }}
            onOpenDetail={onOpenDetail}
          />
        </div>
      )
    }
  }

  // ── Main landing ──
  return (
    <div className="px-4 pb-12 pt-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-6 flex flex-col gap-3 border-b border-white/10 pb-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-text-primary sm:text-3xl lg:text-4xl">
              Awards <span className="text-accent-primary">Season</span>
            </h1>
            <p className="mt-1 text-xs text-text-tertiary sm:text-sm">
              {bodies.length} bodies · every ceremony, every nominee
            </p>
          </div>

          <div
            role="tablist"
            aria-label="Awards views"
            className="flex w-full gap-1 self-start rounded-lg bg-bg-secondary p-1 sm:w-auto"
          >
            <TabButton active={tab === 'ceremonies'} onClick={() => setTab('ceremonies')}>
              Ceremonies
            </TabButton>
            <TabButton
              active={tab === 'leaderboards'}
              onClick={() => setTab('leaderboards')}
            >
              Leaderboards
            </TabButton>
            <TabButton active={tab === 'search'} onClick={() => setTab('search')}>
              Search
            </TabButton>
          </div>
        </header>

        <div className="min-h-[20rem]">
          {tab === 'ceremonies' && (
            <BodyPicker bodies={bodies} onOpen={(id) => setOpenBodyId(id)} />
          )}

          {tab === 'leaderboards' && (
            <LeaderboardSection
              movies={movies}
              people={people}
              onOpenDetail={onOpenDetail}
              onPersonOpen={onPersonOpen}
            />
          )}

          {tab === 'search' && (
            <SearchByFilm index={index} onOpenDetail={onOpenDetail} />
          )}
        </div>
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors sm:flex-none sm:px-5 sm:py-2 sm:text-sm ${
        active
          ? 'bg-accent-primary text-white shadow-md'
          : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * Desktop parity: the desktop shows a spinner, but the PWA prefers a
 * content-shaped skeleton so the layout doesn't jump when data lands.
 */
function AwardsSkeleton() {
  return (
    <div className="animate-pulse px-4 pt-4 pb-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex items-end justify-between border-b border-white/10 pb-4">
          <div>
            <div className="h-8 w-48 rounded bg-bg-secondary" />
            <div className="mt-2 h-3 w-32 rounded bg-bg-secondary" />
          </div>
          <div className="h-10 w-64 rounded-lg bg-bg-secondary" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-48 rounded-2xl bg-bg-secondary" />
          ))}
        </div>
      </div>
    </div>
  )
}
